import Button from '@rio-cloud/rio-uikit/Button';
import Dialog from '@rio-cloud/rio-uikit/Dialog';
import { useState } from 'react';
import { useIntl } from 'react-intl';

import { type RoutingSettings, routingSettingsSchema } from './routingSettings.ts';

const fields = [
    {
        key: 'height_m',
        min: 0.1,
        max: 10,
        ev: false,
    },
    {
        key: 'width_m',
        min: 0.1,
        max: 10,
        ev: false,
    },
    {
        key: 'length_m',
        min: 0.1,
        max: 50,
        ev: false,
    },
    {
        key: 'gross_weight_kg',
        min: 1,
        max: 100000,
        ev: false,
    },
    {
        key: 'current_weight_kg',
        min: 1,
        max: 100000,
        ev: false,
    },
    {
        key: 'axle_weight_kg',
        min: 1,
        max: 30000,
        ev: false,
    },
    {
        key: 'vehicle_axles',
        min: 2,
        max: 10,
        ev: false,
    },
    {
        key: 'trailer_axles',
        min: 0,
        max: 10,
        ev: false,
    },
    {
        key: 'cost_per_km',
        min: 0,
        max: 100,
        ev: false,
    },
    {
        key: 'battery_kwh',
        min: 1,
        max: 2000,
        ev: true,
    },
    {
        key: 'initial_charge_percent',
        min: 1,
        max: 100,
        ev: true,
    },
    {
        key: 'reserve_percent',
        min: 1,
        max: 99,
        ev: true,
    },
    {
        key: 'target_charge_percent',
        min: 2,
        max: 100,
        ev: true,
    },
    {
        key: 'charging_power_kw',
        min: 1,
        max: 2000,
        ev: true,
    },
    {
        key: 'consumption_kwh_per_km',
        min: 0.01,
        max: 10,
        ev: true,
    },
] as const;

export const VehicleRoutingSettingsDialog = ({
    value,
    onApply,
    onClose,
}: {
    value: RoutingSettings;
    onApply: (settings: RoutingSettings) => void;
    onClose: () => void;
}) => {
    const intl = useIntl();
    const t = (key: string) => intl.formatMessage({ id: `intl-msg:ordercom-cloud-frontend.routing-settings.${key}` });
    const [draft, setDraft] = useState(value);
    const valid = routingSettingsSchema.safeParse(draft).success;
    return (
        <Dialog
            show
            onClose={onClose}
            title={t('title')}
            body={
                <div style={{ maxHeight: 'calc(100dvh - 240px)', overflowY: 'auto' }}>
                    <p>{t('assumptions')}</p>
                    <p className='text-size-12'>
                        {intl.formatMessage({ id: 'intl-msg:ordercom-cloud-frontend.smart-route.disclaimer' })}
                    </p>
                    <label className='display-flex gap-10 margin-bottom-20'>
                        <input
                            type='checkbox'
                            checked={draft.electric}
                            onChange={event => {
                                const next = { ...draft, electric: event.target.checked };
                                if (!next.electric) {
                                    for (const field of fields) {
                                        if (field.ev) {
                                            next[field.key] = value[field.key];
                                        }
                                    }
                                }
                                setDraft(next);
                            }}
                        />
                        {t('electric')}
                    </label>
                    <div className='display-grid grid-cols-2 gap-10'>
                        {fields
                            .filter(field => !field.ev || draft.electric)
                            .map(field => (
                                <div key={field.key} className='form-group'>
                                    <label htmlFor={`routing-${field.key}`}>{t(field.key)}</label>
                                    <input
                                        id={`routing-${field.key}`}
                                        className='form-control'
                                        type='number'
                                        min={field.min}
                                        max={field.max}
                                        step={field.key.includes('axles') ? 1 : 'any'}
                                        value={Number.isNaN(draft[field.key]) ? '' : draft[field.key]}
                                        onChange={event =>
                                            setDraft({ ...draft, [field.key]: event.target.valueAsNumber })
                                        }
                                    />
                                </div>
                            ))}
                        {(
                            [
                                ['tunnel_category', ['none', 'B', 'C', 'D', 'E']],
                                [
                                    'hazardous_goods',
                                    [
                                        'none',
                                        'explosive',
                                        'gas',
                                        'flammable',
                                        'combustible',
                                        'organic',
                                        'poison',
                                        'radioActive',
                                        'corrosive',
                                        'poisonousInhalation',
                                        'harmfulToWater',
                                        'other',
                                    ],
                                ],
                                ['emission_type', ['euro6', 'euro5', 'euro4']],
                            ] as const
                        ).map(([key, options]) => (
                            <div key={key} className='form-group'>
                                <label htmlFor={`routing-${key}`}>{t(key)}</label>
                                <select
                                    className='form-control'
                                    id={`routing-${key}`}
                                    value={draft[key]}
                                    onChange={event => setDraft({ ...draft, [key]: event.target.value })}
                                >
                                    {options.map(option => (
                                        <option key={option} value={option}>
                                            {option.startsWith('euro')
                                                ? option.toUpperCase().replace('EURO', 'EURO ')
                                                : option.length === 1
                                                  ? option
                                                  : t(option)}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        ))}
                    </div>
                    {!valid && (
                        <p role='alert' className='text-color-danger'>
                            {t('invalid')}
                        </p>
                    )}
                </div>
            }
            footer={
                <div className='display-flex gap-10 justify-content-end'>
                    <Button onClick={onClose}>{t('cancel')}</Button>
                    <Button
                        bsStyle='primary'
                        disabled={!valid}
                        onClick={() => onApply(routingSettingsSchema.parse(draft))}
                    >
                        {t('apply')}
                    </Button>
                </div>
            }
        />
    );
};
